/**
 * The app's toast API: sonner's `toast`, plus the one DESIGN.md rule sonner cannot express on the
 * <Toaster>: "success auto-dismiss 4s, errors sticky" (DESIGN.md §Motion; eval shell-and-player,
 * toast phone_spec: "errors stay until dismissed with a ×").
 *
 * Sonner has no per-type lifetime. A Toaster-wide `duration: Infinity` makes every toast sticky,
 * not just errors. So an error gets `duration: Infinity` and a close button on the toast itself.
 * Only the per-toast `Infinity` stops sonner's timer. The × is styled in components/ui/sonner.tsx
 * (a 20px glyph with a 44px hit area). Sonner's toaster is a polite live region, so an error is
 * announced without interrupting.
 *
 * Decision 7 (Raghav, 2026-10-20): error toasts stay until dismissed, APP-WIDE. A caller can no
 * longer shorten an error's life or hide its ×; success and neutral toasts keep auto-dismissing.
 *
 * Import `toast` from here, never from "sonner": a direct `toast.error` from sonner vanishes after
 * 4s with no way to dismiss it (src/test/toast-errors-sticky.test.ts fails on any such import).
 * `hooks/use-toast` (the legacy `{ variant: "destructive" }` API) routes through here too.
 */
import { toast as sonnerToast, type ExternalToast } from "sonner";

type ErrorMessage = Parameters<typeof sonnerToast.error>[0];

function stickyError(message: ErrorMessage, data?: ExternalToast) {
  return sonnerToast.error(message, { ...data, duration: Infinity, closeButton: true });
}

export const toast: typeof sonnerToast = Object.assign(
  (...args: Parameters<typeof sonnerToast>) => sonnerToast(...args),
  sonnerToast,
  { error: stickyError }
);
